import React from 'react';

import {useI18n} from '@udemy/i18n';
import {Icon} from '@udemy/icons';
import {Button} from '@udemy/react-core-components';
import {Tooltip} from '@udemy/react-popup-components';

import {normalizeProgressPercent} from '../learning-path-state';
import {LearningPathStepStatus} from '../learning-path-status';

import type {StepStatus} from '../learning-path-status';

interface LearningPathCourseCtaProps {
    status: StepStatus;
    href?: string;
    certificateHref?: string;
    certificateReady?: boolean;
    progressPercent?: number;
    isFirstIncompleteCourse?: boolean;
}

export function LearningPathCourseCta({
    status,
    href,
    certificateHref,
    certificateReady = true,
    progressPercent,
    isFirstIncompleteCourse,
}: LearningPathCourseCtaProps) {
    const {gettext} = useI18n();

    const isCompleted = status === LearningPathStepStatus.Completed;
    const normalizedProgressPercent = normalizeProgressPercent(progressPercent);
    const hasProgress = normalizedProgressPercent > 0;

    // Show a "Start course" CTA only for the first incomplete course, and only when the user has 0% progress.
    const shouldShowStartCourseCta =
        normalizedProgressPercent === 0 && Boolean(isFirstIncompleteCourse) && !isCompleted;

    const shouldRenderCta = isCompleted || hasProgress || shouldShowStartCourseCta;
    const showPlayArrowIcon = !isCompleted && (hasProgress || shouldShowStartCourseCta);

    const isCertificateCta = isCompleted;
    const hasCertificate = Boolean(certificateHref);
    const isCertificateEnabled = isCertificateCta && hasCertificate && certificateReady;
    const resolvedHref = isCertificateCta ? certificateHref : href;

    const shouldRenderCompletedWithoutHref = isCompleted && !resolvedHref;
    if (!shouldRenderCta || (!resolvedHref && !shouldRenderCompletedWithoutHref)) {
        return null;
    }

    const label = isCompleted
        ? gettext('View course certificate')
        : hasProgress
          ? gettext('Resume course')
          : gettext('Start course');

    const isDisabled = isCertificateCta && !isCertificateEnabled;
    const buttonHref = !isCertificateCta || isCertificateEnabled ? resolvedHref : undefined;

    const button = (
        <Button
            componentClass="a"
            href={buttonHref}
            target="_blank"
            rel="noopener noreferrer"
            disabled={isDisabled}
            udStyle={status === LearningPathStepStatus.Completed ? 'link' : 'secondary'}
            style={isDisabled ? undefined : {width: '100%'}}
        >
            {showPlayArrowIcon && (
                <Icon color="info" glyph="play-arrow" label={false} size="small" />
            )}
            <span>{label}</span>
        </Button>
    );

    if (isCertificateCta && !isCertificateEnabled) {
        return (
            <div style={{display: 'flex', justifyContent: 'center', width: '100%'}}>
                <Tooltip detachFromTarget placement="top" trigger={button}>
                    {gettext('Your certificate is being prepared')}
                </Tooltip>
            </div>
        );
    }

    return button;
}
